import React from 'react';
import { Image, Palette, RefreshCw, Save } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { api } from '../../../lib/api';
import { useBranding } from '../../../components/theme/use-branding';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../../components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { MediaPicker } from '../../../components/cms/MediaPicker';

type BrandingForm = {
  companyName: string;
  companyAddress: string;
  logoUrl: string;
  primaryColor: string;
  secondaryColor: string;
  legalCompany: string;
  legalAddress: string;
  legalEmail: string;
  imprintUrl: string;
  privacyUrl: string;
  termsUrl: string;
  legalText: string;
};

export default function BrandingSettings() {
  const [isLoading, setIsLoading] = React.useState(false);
  const [logoPickerOpen, setLogoPickerOpen] = React.useState(false);
  const { branding, refresh } = useBranding();
  const brandingForm = useForm<BrandingForm>({
    defaultValues: {
      companyName: branding.companyName,
      companyAddress: branding.companyAddress,
      logoUrl: branding.logoUrl,
      primaryColor: branding.primaryColor,
      secondaryColor: branding.secondaryColor,
      legalCompany: branding.legalCompany,
      legalAddress: branding.legalAddress,
      legalEmail: branding.legalEmail,
      imprintUrl: branding.imprintUrl,
      privacyUrl: branding.privacyUrl,
      termsUrl: branding.termsUrl,
      legalText: branding.legalText,
    },
  });

  React.useEffect(() => {
    brandingForm.reset({
      companyName: branding.companyName,
      companyAddress: branding.companyAddress,
      logoUrl: branding.logoUrl,
      primaryColor: branding.primaryColor,
      secondaryColor: branding.secondaryColor,
      legalCompany: branding.legalCompany,
      legalAddress: branding.legalAddress,
      legalEmail: branding.legalEmail,
      imprintUrl: branding.imprintUrl,
      privacyUrl: branding.privacyUrl,
      termsUrl: branding.termsUrl,
      legalText: branding.legalText,
    });
  }, [branding, brandingForm]);

  const onSubmitBranding = async (data: BrandingForm) => {
    setIsLoading(true);
    try {
      await api.put<BrandingForm>('/api/settings/branding', data);
      await refresh();
      toast.success('Branding wurde gespeichert');
    } catch (e) {
      toast.error('Fehler beim Speichern des Brandings');
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  return <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Branding</h1>
        <p className="text-muted-foreground">Konfigurieren Sie Logo, Farben und Unternehmensangaben.</p>
      </div>
      <Form {...brandingForm}>
        <form onSubmit={brandingForm.handleSubmit(onSubmitBranding)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Palette className="mr-2 h-5 w-5" />
                Branding
              </CardTitle>
              <CardDescription>Logo, Farben, Name und Adresse für Verwaltung und Website.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField control={brandingForm.control} name="companyName" render={({ field }) => <FormItem>
                    <FormLabel>Unternehmensname</FormLabel>
                    <FormControl>
                      <Input {...field} uiSize="md" placeholder="z. B. Musikschule Beispiel" />
                    </FormControl>
                    <FormDescription>Wird u. a. oben im Header angezeigt.</FormDescription>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={brandingForm.control} name="companyAddress" render={({ field }) => <FormItem>
                    <FormLabel>Adresse</FormLabel>
                    <FormControl>
                      <Input {...field} uiSize="md" placeholder="Straße Hausnr., PLZ Ort" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={brandingForm.control} name="logoUrl" render={({ field }) => <FormItem>
                    <FormLabel>Logo</FormLabel>
                    <FormControl>
                      <div className="flex items-start gap-3">
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded border bg-white">
                          {field.value ? (
                            <img src={field.value} alt="" className="h-full w-full object-contain" />
                          ) : (
                            <Image className="h-6 w-6 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1 space-y-2">
                          <Input {...field} uiSize="md" placeholder="https://.../logo.png" />
                          <div className="flex gap-2">
                            <Button type="button" variant="outline" size="sm" onClick={() => setLogoPickerOpen(true)}>
                              <Image className="mr-2 h-4 w-4" /> Aus Bibliothek
                            </Button>
                            {field.value && (
                              <Button type="button" variant="ghost" size="sm" onClick={() => field.onChange('')}>
                                Entfernen
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </FormControl>
                    <FormDescription>Wird im Header und Login angezeigt – aus der Mediathek wählbar.</FormDescription>
                    <FormMessage />
                    <MediaPicker
                      open={logoPickerOpen}
                      onClose={() => setLogoPickerOpen(false)}
                      onSelect={(url) => field.onChange(url)}
                    />
                  </FormItem>} />
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField control={brandingForm.control} name="primaryColor" render={({ field }) => <FormItem>
                      <FormLabel>Primärfarbe (Hex)</FormLabel>
                      <FormControl>
                        <Input {...field} uiSize="md" placeholder="#4485df" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
                <FormField control={brandingForm.control} name="secondaryColor" render={({ field }) => <FormItem>
                      <FormLabel>Sekundärfarbe (Hex)</FormLabel>
                      <FormControl>
                        <Input {...field} uiSize="md" placeholder="#6b7280" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField control={brandingForm.control} name="legalCompany" render={({ field }) => <FormItem>
                      <FormLabel>Rechtlicher Firmenname</FormLabel>
                      <FormControl>
                        <Input {...field} uiSize="md" placeholder="z. B. Muster Musikschule GmbH" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
                <FormField control={brandingForm.control} name="legalEmail" render={({ field }) => <FormItem>
                      <FormLabel>Rechtliche Kontakt-E-Mail</FormLabel>
                      <FormControl>
                        <Input {...field} uiSize="md" placeholder="info@beispiel.de" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>
              <FormField control={brandingForm.control} name="legalAddress" render={({ field }) => <FormItem>
                    <FormLabel>Rechtliche Adresse</FormLabel>
                    <FormControl>
                      <Input {...field} uiSize="md" placeholder="Straße Hausnr., PLZ Ort" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <FormField control={brandingForm.control} name="imprintUrl" render={({ field }) => <FormItem>
                      <FormLabel>Impressum URL</FormLabel>
                      <FormControl>
                        <Input {...field} uiSize="md" placeholder="https://.../impressum" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
                <FormField control={brandingForm.control} name="privacyUrl" render={({ field }) => <FormItem>
                      <FormLabel>Datenschutz URL</FormLabel>
                      <FormControl>
                        <Input {...field} uiSize="md" placeholder="https://.../datenschutz" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
                <FormField control={brandingForm.control} name="termsUrl" render={({ field }) => <FormItem>
                      <FormLabel>AGB URL</FormLabel>
                      <FormControl>
                        <Input {...field} uiSize="md" placeholder="https://.../agb" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>
              <FormField control={brandingForm.control} name="legalText" render={({ field }) => <FormItem>
                    <FormLabel>Zusätzlicher Rechtstext</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Optionaler zusätzlicher Rechtstext im E-Mail-Footer" />
                    </FormControl>
                    <FormDescription>Wird im E-Mail-Footer unter den rechtlichen Kontaktdaten angezeigt.</FormDescription>
                    <FormMessage />
                  </FormItem>} />
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded" style={{ background: brandingForm.watch('primaryColor') }} />
                <div className="h-10 w-10 rounded" style={{ background: brandingForm.watch('secondaryColor') }} />
                <div className="ml-4 flex items-center gap-2">
                  <Image className="h-5 w-5" />
                  <span className="text-sm text-muted-foreground">Logo-Vorschau über Header</span>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-end space-x-4">
              <Button type="submit" disabled={isLoading}>
                {isLoading ? <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Speichern...
                  </> : <>
                    <Save className="mr-2 h-4 w-4" />
                    Branding speichern
                  </>}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>;
}

